// ============================================================================
// view.weighins — the /weighins collection form, reading locally
// ============================================================================
// The weigh-in form is login-required but not program-scoped: every
// authenticated user including Light reaches it, and it collects for all four
// species from one component behind a species -> select -> session -> done
// stage machine. Its ten physical reads were six independent effects, and they
// are six catalog resources now, one per effect.
//
// WHY SIX AND NOT ONE.
//
// The neighbouring record page (view.weighinsessions) collapses ten reads into
// three, because loadAll ran them as one chain with one failure. This form did
// the opposite: six requests that never depended on each other, each publishing
// into its own state with its own failure posture. Merging any two would couple
// failures that are not coupled today —
//
//   weighin.webform.speciesDirectory   the picked species' animal directory
//                                      (cattle/sheep) or batch-name list
//                                      (pig/broiler). Throws; a failed read
//                                      leaves the previous value, which is what
//                                      `if (data) setCattleList(data)` did.
//   weighin.webform.broilerSchooners   the public broiler schooner mirror.
//                                      FAIL-SOFT and separate, because its
//                                      settled marker is what makes Start
//                                      Session fail CLOSED on a batch with no
//                                      resolvable schooners — it was set from a
//                                      `.finally()`, so a sibling read's
//                                      failure must never withhold it.
//   weighin.webform.drafts             the seven-day resumable-draft list.
//   weighin.webform.priorWeights       the completed-session + entry fold that
//                                      indexes the latest weight per tag.
//                                      FAIL-SOFT to an empty map, because both
//                                      of its failure paths published `{}`.
//   weighin.webform.sessionEntries     the open session's persisted rows, in
//                                      entered_at insertion order.
//   weighin.webform.pigMetrics         the pig_session_metrics RPC for the one
//                                      open session, keyed by entry count.
//
// WHY THE PRESENTER CONSUMES THEM DIRECTLY, WITH NO BRIDGE.
//
// A bridge exists to inject an authenticated IDENTITY into a webform that must
// not import AuthContext (see AddFeedAuthenticatedBridge). This form already
// receives that identity as the `sessionSubmitter` prop from WebformsEntry, and
// it still imports no AuthContext — useRouteResource owns the scope. A bridge
// could not own these resources anyway: every one of them is keyed on state
// that lives INSIDE the stage machine (which species was picked, which session
// is open, how many entries have been weighed), so lifting them above the
// presenter would mean lifting the stage machine with them. FuelingHub and
// EquipmentFuelingWebform are the existing precedent for a src/webforms
// presenter holding its own route resources.
//
// WHY THE RESIDUAL LIST IS EMPTY.
//
// `weighins` sits in the noFormerEagerCohort of
// tests/fixtures/former-eager-dependencies.json — one of the 29 authenticated
// views the map proves consume no result from any of App's five former-eager
// reads. It reads no App context and receives no boot array; its only prop is
// the identity. So `[]` is derived, not omitted.
//
// The offline write path is untouched and is not an obligation. Fresh pig and
// broiler sessions still submit through useOfflineRpcSubmit against
// submit_weigh_in_session_batch, cattle/sheep still write directly, and the
// completion flow still stamps through its RPC. A command is not a read.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.weighins',
  obligations: Object.freeze([
    Object.freeze({id: 'weighin.webform.speciesDirectory', owner: 'permanent'}),
    Object.freeze({id: 'weighin.webform.broilerSchooners', owner: 'permanent'}),
    Object.freeze({id: 'weighin.webform.drafts', owner: 'permanent'}),
    Object.freeze({id: 'weighin.webform.priorWeights', owner: 'permanent'}),
    Object.freeze({id: 'weighin.webform.sessionEntries', owner: 'permanent'}),
    Object.freeze({id: 'weighin.webform.pigMetrics', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
